import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../environments/environment';
import { appConstants } from '../app-constants';
import { Router } from '@angular/router';
import { ToastService } from '../toast/toast.service';
import { CreateTicketGQL, ValidationErrorFieldsFragment } from '../graphql/gql';
import { AppValidationHelpers } from '../app-validation-helpers';
import { NgxTurnstileComponent } from 'ngx-turnstile';
import { AppValidators } from '../app-validators';

/**
 * NewTicketComponent displays a public contact form and submits it via GraphQL.
 *
 */
@Component({
  selector: 'app-new-ticket',
  templateUrl: './new-ticket.component.html',
  styleUrls: ['./new-ticket.component.css'],
  standalone: false
})
export class NewTicketComponent implements OnInit {
  form!: FormGroup;
  submitted = false;
  turnstileSiteKey = environment.turnstileSiteKey;
  captchaToken: string | null = null;
  @ViewChild(NgxTurnstileComponent) turnstile?: NgxTurnstileComponent;
  getRemoteValidationMessages = AppValidationHelpers.getRemoteValidationMessages;

  constructor(
    private formBuilder: FormBuilder,
    private createTicketGQL: CreateTicketGQL,
    private router: Router,
    private toastService: ToastService
  ) {
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.pattern(AppValidators.PHONE_REGEX)],
      message: ['', Validators.required]
    });
  }

  onCaptchaResolved(token: string | null): void {
    this.captchaToken = token;
  }

  async onSubmit(): Promise<any> {
    this.submitted = true;
    this.form.markAllAsTouched();

    // stop here if form is invalid or captcha not completed
    if (this.form.invalid) {
      this.submitted = false;
      return;
    }

    if (!this.captchaToken) {
      this.toastService.show('error', 'Please complete the CAPTCHA verification');
      this.submitted = false;
      return;
    }

    const input = {
      firstname: this.form.value.firstname || undefined,
      lastname: this.form.value.lastname,
      email: this.form.value.email,
      phone: this.form.value.phone || undefined,
      message: this.form.value.message
    };

    this.createTicketGQL.mutate({
      input,
      turnstileToken: this.captchaToken as string
    }).subscribe({
      next: ({ data }) => {
        const creationResult = data?.createTicket as any;
        if (!creationResult) {
          this.handleError('An unexpected error occurred while submitting the ticket.');
          return;
        }

        switch (creationResult.__typename) {
          case 'Ticket': {
            this.form.reset();
            this.submitted = false;
            this.resetCaptcha();
            this.toastService.show('success', $localize`Ticket submitted successfully`);
            this.router.navigateByUrl('/login');
            break;
          }

          case 'ValidationErrors': {
            const validationMessages = (creationResult.validationMessages ?? []) as ValidationErrorFieldsFragment[];

            if (validationMessages.length !== 0) {
              const unmapped = AppValidationHelpers.applyValidationErrors(validationMessages, this.form);
              this.handleValidationErrors(AppValidationHelpers.unmappedErrorMessage(unmapped));
            }

            break;
          }

          default: {
            this.handleError('An unexpected error occurred while submitting the ticket.');
            break;
          }
        }
      }
    });
  }

  logoUrl(): string {
    return appConstants.LOGO_URL;
  }

  /**
   * Reports the errors which could not be attached to a form control and resets the captcha.
   *
   * The captcha error is one such error — `turnstileToken` has no control in this form — so it is
   * covered by the generic message rather than a case of its own. See BNDDEV-10062.
   *
   * @param {string | null} unmappedMessage - The errors with no field to render under, if any.
   */
  private handleValidationErrors(unmappedMessage: string | null) {
    this.submitted = false;

    if (unmappedMessage !== null) {
      this.toastService.show('error', unmappedMessage);
    }

    this.resetCaptcha();
  }

  private handleError(message: string, error?: unknown) {
    this.submitted = false;
    this.resetCaptcha();
    this.toastService.show('error', message);
    console.error(error);
  }

  private resetCaptcha(): void {
    this.captchaToken = null;
    this.turnstile?.reset();
  }
}
